import "./campaign-response-row.style.scss";
import { Col, View } from "@amzn/stencil-react-components/layout";
import { Text, TextVariant } from "@amzn/stencil-react-components/text";
import { Select } from "@amzn/stencil-react-components/select";
import { Input, InputHeader } from "@amzn/stencil-react-components/input";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import React, { FC } from "react";
import { RESPONSE_OPTIONS, DEFAULT_EVIDENCE_LABEL } from "../campaign-urgency";
import type { CampaignResponseValue } from "../campaign-urgency";
import type { Asset } from "@amzn/hardware-self-service-api-model";

type ResponseOption = (typeof RESPONSE_OPTIONS)[number];

const RESPONSE_OPTIONS_DATA_ATTRIBUTE = "data-hss-response-options";

interface ResponseOptionListItemProps {
  option: ResponseOption;
}

const ResponseOptionListItem: FC<ResponseOptionListItemProps> = ({ option }) => (
  <Col gridGap="S050" padding={["S100", "S200"]} alignItems="flex-start">
    <Text variant={TextVariant.LabelMD}>{option.label}</Text>
    <Text variant={TextVariant.LabelXS} fontStyle="italic" color="neutral70">
      {option.description}
    </Text>
  </Col>
);

const renderResponseOption = (option: ResponseOption) => <ResponseOptionListItem option={option} />;

const renderResponseSelectedValue = (option: ResponseOption) => option.label;

const renderResponseNativeOption = (option: ResponseOption) => option.label;

interface CampaignResponseRowProps {
  asset: Asset;
  value: CampaignResponseValue;
  evidence: string;
  disabled?: boolean;
  onResponseChange: (value: CampaignResponseValue) => void;
  onEvidenceChange: (value: string) => void;
}

export const CampaignResponseRow: FC<CampaignResponseRowProps> = ({
  asset,
  value,
  evidence,
  disabled = false,
  onResponseChange,
  onEvidenceChange,
}) => {
  const selectedOption = RESPONSE_OPTIONS.find((option) => option.value === value);
  const requiresEvidence = selectedOption?.requiresEvidence ?? false;
  const evidenceLabel = selectedOption?.evidenceLabel ?? DEFAULT_EVIDENCE_LABEL;
  const unknown = getString("campaigns.unknown");
  const deviceName =
    asset.make || asset.model
      ? `${asset.make ?? unknown} ${asset.model ?? unknown}`
      : getString("campaigns.unknownDevice");
  const serialNumber = asset.serialNumber ?? unknown;

  return (
    <View padding="S400" border="1px solid var(--st-dt-color-neutral-20)" borderRadius="R400">
      <Col gridGap="S300">
        <Col gridGap="S100">
          <Text fontWeight="bold">{deviceName}</Text>
          <Text variant={TextVariant.BodySM} color="neutral70">
            {getString("campaigns.serialNumberPrefix")} {serialNumber}
          </Text>
        </Col>
        <Select
          dataTestId={`response-${serialNumber}`}
          value={selectedOption}
          onChange={(option: ResponseOption) => onResponseChange(option.value)}
          options={RESPONSE_OPTIONS}
          valueAccessor="value"
          renderOption={renderResponseOption}
          renderNativeOption={renderResponseNativeOption}
          renderSelectedValue={renderResponseSelectedValue}
          placeholder={getString("campaigns.selectResponse")}
          width="100%"
          listMaxHeight="15em"
          listPositioningStrategy="fixed"
          disabled={disabled}
          slotProps={{ optionList: { [RESPONSE_OPTIONS_DATA_ATTRIBUTE]: "true" } }}
        />
        {requiresEvidence && (
          <Col gridGap="S100">
            <InputHeader htmlFor={`evidence-${serialNumber}`} labelText={evidenceLabel} />
            <Input
              id={`evidence-${serialNumber}`}
              dataTestId={`evidence-${serialNumber}`}
              placeholder={getString("campaigns.evidencePlaceholder")}
              value={evidence}
              disabled={disabled}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onEvidenceChange(event.target.value)
              }
            />
          </Col>
        )}
      </Col>
    </View>
  );
};
